import { useEffect, useState } from "react";

// ─────────── Countdown timer ───────────
export const FlashCountdown = ({ hours = 6 }) => {
  const [endsAt] = useState(() => {
    const stored = sessionStorage.getItem("wfw_flash_end");
    if (stored) return parseInt(stored, 10);
    const end = Date.now() + hours * 3600 * 1000;
    sessionStorage.setItem("wfw_flash_end", end);
    return end;
  });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, endsAt - now);
  const hh = String(Math.floor(remaining / 3600000)).padStart(2, "0");
  const mm = String(Math.floor((remaining % 3600000) / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, "0");

  return (
    <div
      className="flex items-center justify-between gap-4 bg-black text-white px-4 py-3"
      data-testid="flash-countdown"
    >
      <div className="flex items-center gap-2">
        <span className="inline-block w-2 h-2 bg-red-400 rounded-full animate-pulse" />
        <span className="text-[11px] tracking-[0.2em] uppercase font-medium">
          Bulk pricing ends in
        </span>
      </div>
      <div className="flex items-center gap-1 font-heading">
        <span className="bg-white text-black px-2 py-1 text-sm font-black tracking-tight">{hh}</span>
        <span className="font-bold">:</span>
        <span className="bg-white text-black px-2 py-1 text-sm font-black tracking-tight">{mm}</span>
        <span className="font-bold">:</span>
        <span className="bg-white text-black px-2 py-1 text-sm font-black tracking-tight">{ss}</span>
      </div>
    </div>
  );
};

// ─────────── Live viewer count ───────────
export const ViewerBadge = () => {
  const [count, setCount] = useState(() => 12 + Math.floor(Math.random() * 28));
  useEffect(() => {
    const id = setInterval(() => {
      setCount((c) => Math.max(8, Math.min(60, c + (Math.random() > 0.5 ? 1 : -1))));
    }, 4500);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="flex items-center gap-2 text-xs text-neutral-600"
      data-testid="viewer-badge"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <span>
        <span className="font-bold text-black">{count} clinics</span> viewing this now
      </span>
    </div>
  );
};

// ─────────── Stock-left urgency ───────────
export const StockBadge = ({ initial = 14 }) => {
  const [n] = useState(initial);
  return (
    <div className="flex items-center gap-3" data-testid="stock-badge">
      <div className="flex-1 h-1 bg-neutral-200 relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-red-500"
          style={{ width: `${Math.min(100, (n / 50) * 100)}%` }}
        />
      </div>
      <span className="text-xs font-bold text-red-600 whitespace-nowrap">
        Only {n} left
      </span>
    </div>
  );
};

// ─────────── Recent purchases ticker ───────────
const FAKE_BUYERS = [
  { name: "Dr. Sofia H.", city: "Madrid", time: "2 min ago", qty: 5 },
  { name: "Clinique Vermeil", city: "Paris", time: "8 min ago", qty: 3 },
  { name: "Dr. Marcus R.", city: "London", time: "14 min ago", qty: 12 },
  { name: "Dermalife Group", city: "Milan", time: "22 min ago", qty: 8 },
  { name: "Dr. Anya K.", city: "Berlin", time: "33 min ago", qty: 4 },
  { name: "Beauté Lab", city: "Lyon", time: "41 min ago", qty: 20 },
  { name: "Dr. Carlos M.", city: "Barcelona", time: "1 h ago", qty: 6 },
  { name: "Aesthetica Studio", city: "Amsterdam", time: "1 h ago", qty: 10 },
];

export const RecentPurchasesTicker = ({ productName }) => {
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let t1, t2;
    const tick = () => {
      setVisible(true);
      t1 = setTimeout(() => setVisible(false), 4500);
      t2 = setTimeout(() => {
        setIdx((i) => (i + 1) % FAKE_BUYERS.length);
        tick();
      }, 7500);
    };
    const initial = setTimeout(tick, 2500);
    return () => {
      clearTimeout(initial);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const b = FAKE_BUYERS[idx];

  return (
    <div
      className={`fixed bottom-6 left-6 z-40 max-w-xs bg-white border border-neutral-200 shadow-2xl pointer-events-none transition-all duration-500 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
      }`}
      data-testid="recent-purchase-ticker"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
          <span className="text-emerald-700 text-lg">✓</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-neutral-500 font-medium">Verified purchase</p>
          <p className="text-sm text-black font-bold leading-tight">
            {b.name} · {b.city}
          </p>
          <p className="text-xs text-neutral-600 leading-tight mt-1">
            Bought {b.qty} × {productName}
          </p>
          <p className="text-[10px] text-neutral-400 mt-1 tracking-wide uppercase">
            {b.time}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─────────── Trust strip ───────────
export const TrustStrip = () => (
  <div className="grid grid-cols-3 gap-px bg-neutral-200" data-testid="trust-strip">
    {[
      { label: "Free shipping", sub: "Worldwide" },
      { label: "Cold-chain", sub: "Guaranteed" },
      { label: "Authentic", sub: "Lot verified" },
    ].map((t) => (
      <div key={t.label} className="bg-white px-3 py-3 text-center">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black">
          {t.label}
        </div>
        <div className="text-[10px] text-neutral-500 mt-0.5">{t.sub}</div>
      </div>
    ))}
  </div>
);
